import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpHeaders, HttpErrorResponse, HttpClient } from "@angular/common/http";
import { catchError } from "rxjs/operators";
import { throwError, Observable } from "rxjs";
import { GlobalService } from "../shared/global.service";
import { StorageService } from "./storage.service";
import { User } from "../entities/users";
@Injectable()
export class AuthService {
  private baseUrl: string;

  // ######################################
  // FUNCTION: constructor
  constructor(
    private myRoute: Router,
    private http: HttpClient,
    private g: GlobalService,
    private storage: StorageService
  ) {
    this.baseUrl = this.g.nodePath;
  }

  // ######################################
  // FUNCTION: saveLoginResponse
  // SECURITY: salva user (senza pwd) e token JWT separatamente
  saveLoginResponse(response: { user: User; token: string }) {
    this.saveUser(response.user);
    this.storage.saveToken(response.token);
    this.storage.saveApiToken(response.token);
  }

  // ######################################
  // FUNCTION: saveUser
  // SECURITY: strip password before persisting. Usiamo `localStorage` (e non
  // sessionStorage) per mantenere la sessione admin tra chiusure del browser
  // su mobile — scadenza di fatto gestita dalla JWT (31 giorni).
  saveUser(user: User) {
    const { pwd, temp_code, scadenza_temp_code, ...safeUser } = user as any;
    localStorage.setItem("LoggedInUser", btoa(JSON.stringify(safeUser)));
    // Compat: alcune parti legacy del codice leggevano da sessionStorage.
    // Manteniamo anche lì finché non migriamo tutti i consumer.
    sessionStorage.setItem("LoggedInUser", btoa(JSON.stringify(safeUser)));
  }

  // ######################################
  // FUNCTION: getUser
  getUser(): User | null {
    const loggedIn = localStorage.getItem("LoggedInUser") || sessionStorage.getItem("LoggedInUser");
    if (!loggedIn) return null;
    try {
      return JSON.parse(atob(loggedIn)) as User;
    } catch {
      localStorage.removeItem("LoggedInUser");
      sessionStorage.removeItem("LoggedInUser");
      return null;
    }
  }

  // ######################################
  // FUNCTION: resetUser
  // Rimozione targetizzata: non usiamo `sessionStorage.clear()` per non
  // cancellare stati non correlati (es. compilazioni in corso).
  resetUser() {
    localStorage.removeItem("LoggedInUser");
    sessionStorage.removeItem("LoggedInUser");
    this.storage.saveToken('');
    this.storage.clearApiToken();
    // Chiavi residue legate all'utente (id admin da login.component, letti da
    // NodeService.crypto; `user` legacy di StorageService.setUser). Preferenze
    // non personali (lingua, layout Amajor*Interface) restano.
    sessionStorage.removeItem("id");
    sessionStorage.removeItem("key");
    sessionStorage.removeItem("email");
    localStorage.removeItem("user");
  }

  // ######################################
  // FUNCTION: isLoggedIn
  // Richiede LoggedInUser + apiToken presente e non scaduto (fail-closed in
  // StorageService.isApiTokenExpired): altrimenti pulisce lo storage e ritorna
  // false (il guard farà il redirect al login).
  isLoggedIn() {
    if (this.getUser() === null) return false;
    if (this.storage.isApiTokenExpired()) {
      this.resetUser();
      return false;
    }
    return true;
  }

  // ######################################
  // FUNCTION: logout
  logout() {
    this.resetUser();
    this.myRoute.navigate(["admin/login"]);
    this.g.actualPage = "login";
  }

  // ######################################
  // FUNCTION: checkLogin
  checkLogin(formData: any): Observable<Error | any> {
    const header = new HttpHeaders();
    header.append("Content-Type", "application/json");
    return this.http.post<any>(this.baseUrl + "/users/login", formData).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred
          const details = { detail: err.error, status: err.status };
          return throwError(details);
        } else {
          // A backend error occured (err.status = 500)
          const details = { detail: err.error, status: err.status };
          return throwError(details);
        }
      })
    );
  }
}
